import { Tabs as RadixTabs } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { CountPill } from './CountPill';

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Mała liczba obok etykiety (np. „Zadania 6”). */
  count?: number;
  disabled?: boolean;
}

/**
 * Zakładki z podkreśleniem (jak w profilu klienta). Panele: <TabPanel value="…">.
 * Nawigacja strzałkami i Home/End (Radix Tabs).
 */
export function Tabs({
  value,
  onValueChange,
  items,
  children,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  items: TabItem[];
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange} className={className}>
      <RadixTabs.List
        aria-label={ariaLabel}
        className="flex items-end gap-6 overflow-x-auto border-b border-line [scrollbar-width:none]"
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(
              '-mb-px inline-flex h-10 shrink-0 items-start gap-1 border-b-2 border-transparent pt-1.5 text-body-s whitespace-nowrap text-fg-muted',
              'transition-colors hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-accent',
              'data-[state=active]:border-fg data-[state=active]:font-medium data-[state=active]:text-fg',
              'disabled:opacity-50',
            )}
          >
            {item.label}
            {/* lifting 17.10.2026: licznik jako mała pigułka (CountPill) */}
            {item.count !== undefined && <CountPill count={item.count} className="mt-px ml-0.5" />}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

export function TabPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <RadixTabs.Content value={value} className={cn('pt-5 focus-visible:outline-none', className)}>
      {children}
    </RadixTabs.Content>
  );
}
